(function () {
  "use strict";

  // Browser-side diagnostics for the portal — the missing half of the error story.
  //
  // Before this file existed, app.js's installFriendlyErrorHandler caught uncaught
  // errors and unhandled rejections, showed the user a toast, and wrote the stack
  // to THAT USER'S devtools console. Nobody on our side ever saw it. Every "the
  // page just broke" report was unreproducible by construction. This module ships
  // those same events to POST /api/client-errors, where they land in
  // portal.system_logs next to the backend's records and join on requestId.
  //
  // It is deliberately a SEPARATE FILE rather than more code in app.js: app.js is
  // 52k lines under concurrent edit, and telemetry that can't be reasoned about in
  // isolation is telemetry nobody will trust.
  //
  // ── PHI RULES (this is a healthcare app; logs leave the compliance boundary) ──
  // Portal URLs carry biller search terms — /api/tracker/query?search=<patient
  // name> is a normal request — so:
  //   * every URL is reduced to its PATH before it is queued. Query strings and
  //     fragments are DROPPED, not scrubbed. The server drops them again.
  //   * response bodies, request bodies, form values, DOM text and localStorage
  //     are never read. Only the shape of a failure travels: kind, status, route,
  //     timing, and the error's own name/message.
  //   * error MESSAGES are shape-redacted here (emails, SSNs, dates, long digit
  //     runs) before they cross the wire, so PHI never leaves the browser even if
  //     an exception was constructed with a patient name in it. The server
  //     re-scrubs everything independently — neither pass is trusted alone.
  //
  // ── SAFETY RULES ──
  //   * A LOOP GUARD. Reporting must never report itself: a failure inside the
  //     transport, or an error thrown while building a report, is dropped
  //     silently. Without this, one broken request becomes an infinite POST loop
  //     that DDoSes our own API from every open tab.
  //   * A per-pageload cap. A render loop can throw thousands of times a second;
  //     we send the first MAX_EVENTS and then only a count of what was dropped.
  //   * Deduplication by signature, so one error repeating 400 times is one row.
  //   * Batched and sent on idle/unload, never synchronously in the error path.
  //   * Everything is wrapped in try/catch. Telemetry may not break the app.

  if (typeof window === "undefined" || window.__tmsClientTelemetry) return;

  var MAX_EVENTS = 20;          // per pageload, then we only count
  var MAX_BATCH = 10;
  var FLUSH_DELAY_MS = 2000;
  var DEDUPE_WINDOW_MS = 10000;
  var SLOW_FETCH_MS = 15000;

  var queue = [];
  var sentCount = 0;
  var droppedCount = 0;
  var recentSignatures = Object.create(null);
  var flushTimer = null;
  var reporting = false;        // the loop guard
  var sessionId = createSessionId();

  // ── redaction (mirrors backend/portalSystemLogger.js) ──────────────────────
  var SCRUBBERS = [
    [/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[email]"],
    [/\b\d{3}-?\d{2}-?\d{4}\b/g, "[ssn]"],
    [/\b(?:\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[/-]\d{1,2}[/-]\d{2,4})\b/g, "[date]"],
    [/\b[A-Za-z]{2,}\d{4,}[A-Za-z0-9]*\b/g, "[id]"],
    [/\b\d{6,}\b/g, "[number]"]
  ];

  function redact(value, max) {
    var out = String(value === null || value === undefined ? "" : value);
    // Strip query strings wherever they appear inside free text (stack traces
    // and messages routinely embed a full URL).
    //
    // Consume to END OF LINE, not to the first space. The old class stopped at
    // whitespace, so /statements?search=John Smith&org=5 kept "Smith&org=5" -
    // a bare surname, the one identifier no server-side scrubber can recognise.
    // End-of-LINE rather than end-of-string on purpose: this runs on stacks
    // while they still have newlines, and our deployed assets carry ?v=<hash>
    // cache-busters, so a greedy stripper would eat every frame after the first.
    out = out.replace(/([?#])[^\n]*/g, "$1[query]");
    for (var i = 0; i < SCRUBBERS.length; i += 1) out = out.replace(SCRUBBERS[i][0], SCRUBBERS[i][1]);
    return out.slice(0, max || 500);
  }

  // Reduce any URL to a path. Query and fragment are dropped outright — see the
  // PHI rules above. Long numeric segments are masked so a path-embedded id
  // can't ride along either.
  function pathOf(value) {
    try {
      var raw = String(value === null || value === undefined ? "" : value);
      if (!raw) return "";
      var pathname;
      try {
        pathname = new URL(raw, window.location.origin).pathname;
      } catch (_error) {
        pathname = raw.split(/[?#]/)[0];
      }
      return pathname.split("/").map(function (segment) {
        return /^\d{4,}$/.test(segment) ? "[id]" : segment;
      }).join("/").slice(0, 255);
    } catch (_error) {
      return "";
    }
  }

  function createSessionId() {
    try {
      var bytes = new Uint8Array(8);
      (window.crypto || window.msCrypto).getRandomValues(bytes);
      return Array.prototype.map.call(bytes, function (b) {
        return ("0" + b.toString(16)).slice(-2);
      }).join("");
    } catch (_error) {
      return String(Date.now().toString(16) + Math.random().toString(16).slice(2, 10)).replace(/[^a-f0-9]/g, "");
    }
  }

  // An alphanumeric fingerprint of the failure. Alphanumeric ON PURPOSE: the
  // server's logger allowlists correlation tokens of exactly that shape, so this
  // survives scrubbing intact and lets the digest group repeats. Anything with a
  // separator would be masked to "[number]" — the exact bug VOB hit with React
  // error digests, which cost them most of the value of both error sinks.
  function signatureOf(parts) {
    var text = parts.join("|");
    var hash = 5381;
    for (var i = 0; i < text.length; i += 1) hash = ((hash * 33) ^ text.charCodeAt(i)) >>> 0;
    return hash.toString(36);
  }

  function appCommit() {
    try {
      return String(window.TMS_APP_BUILD_COMMIT || "").replace(/[^A-Za-z0-9]/g, "").slice(0, 40);
    } catch (_error) {
      return "";
    }
  }

  function apiBase() {
    try {
      return String(window.TMS_WORKFLOW_API_BASE || window.TMS_TRACKER_API_BASE || "");
    } catch (_error) {
      return "";
    }
  }

  // The keys app.js keeps the portal bearer token under. Read directly rather
  // than through app.js so telemetry keeps working when app.js is the thing
  // that broke — which is the case this whole module exists for.
  var AUTH_TOKEN_KEYS = ["tms.masterPortal.authToken", "tms.masterPortal.authToken.session"];

  // WHY THE BODY AND NOT AN AUTHORIZATION HEADER.
  // The API is a different origin from the static app, so an Authorization
  // header would turn this into a CORS-preflighted request. The comment on the
  // fetch below is not decoration: text/plain with no custom header is what
  // keeps it a SIMPLE request, and that is what lets a report survive an
  // unload. sendBeacon cannot set headers at all, so a header would silently
  // drop attribution on exactly the pagehide path that carries a crash the user
  // clicked away from.
  //
  // The token is CLAIMED here and PROVEN server-side: backend/clientErrorIngest
  // resolves it through the principal directory, so a forged value attributes
  // nothing. Nothing derived from it is ever echoed back, and the ingest's field
  // allowlist means the token itself can never reach a log record.
  function authToken() {
    for (var i = 0; i < AUTH_TOKEN_KEYS.length; i += 1) {
      try {
        var stores = [window.localStorage, window.sessionStorage];
        for (var s = 0; s < stores.length; s += 1) {
          if (!stores[s]) continue;
          var value = stores[s].getItem(AUTH_TOKEN_KEYS[i]);
          if (value) return String(value).slice(0, 500);
        }
      } catch (_error) { /* storage can throw in private mode */ }
    }
    return "";
  }

  // ── queueing ───────────────────────────────────────────────────────────────
  function report(event) {
    // THE LOOP GUARD. Anything that happens while we are transmitting — including
    // an error thrown by our own fetch wrapper on the telemetry request itself —
    // is dropped without a trace. A reporter that reports its own failures never
    // stops reporting.
    if (reporting) return;
    try {
      if (!event) return;
      var signature = signatureOf([
        event.kind || "",
        event.name || "",
        event.message || "",
        event.route || "",
        event.line || "",
        String(event.status || "")
      ]);
      var now = Date.now();
      var lastSeen = recentSignatures[signature];
      if (lastSeen && now - lastSeen < DEDUPE_WINDOW_MS) {
        droppedCount += 1;
        return;
      }
      recentSignatures[signature] = now;

      if (sentCount >= MAX_EVENTS) {
        droppedCount += 1;
        return;
      }
      sentCount += 1;

      event.stackHash = signature;
      event.sessionId = sessionId;
      event.route = event.route || pathOf(window.location.pathname);
      var commit = appCommit();
      if (commit) event.appCommit = commit;
      try {
        event.viewport = window.innerWidth + "x" + window.innerHeight;
        event.language = String(navigator.language || "").slice(0, 20);
        event.userAgent = String(navigator.userAgent || "").slice(0, 200);
        event.online = navigator.onLine !== false;
      } catch (_error) { /* environment detail is optional */ }

      queue.push(event);
      scheduleFlush(queue.length >= MAX_BATCH);
    } catch (_error) {
      // Building a report must never throw into the app.
    }
  }

  function scheduleFlush(immediate) {
    try {
      if (immediate) return flush();
      if (flushTimer) return;
      flushTimer = window.setTimeout(function () {
        flushTimer = null;
        flush();
      }, FLUSH_DELAY_MS);
    } catch (_error) { /* ignore */ }
  }

  function flush(useBeacon) {
    if (!queue.length) return;
    var batch = queue.splice(0, MAX_BATCH);
    if (droppedCount > 0) {
      batch[batch.length - 1].droppedCount = droppedCount;
      droppedCount = 0;
    }
    var payload;
    try {
      var body = { events: batch };
      // Attribution is OPTIONAL by design: a crash on the login page has no
      // token and must still be reported.
      var claimedToken = authToken();
      if (claimedToken) body.authToken = claimedToken;
      payload = JSON.stringify(body);
    } catch (_error) {
      return;
    }
    var url = apiBase() + "/api/client-errors";

    reporting = true;
    try {
      // text/plain keeps this a CORS "simple request": no preflight, which is
      // what makes it survive an unload. The server parses the body as JSON
      // regardless of the declared content type.
      var sent = false;
      if (useBeacon && navigator.sendBeacon) {
        try {
          sent = navigator.sendBeacon(url, new Blob([payload], { type: "text/plain;charset=UTF-8" }));
        } catch (_error) {
          sent = false;
        }
      }
      if (!sent) {
        window.fetch(url, {
          method: "POST",
          headers: { "content-type": "text/plain;charset=UTF-8" },
          body: payload,
          keepalive: true,
          credentials: "omit"
        }).catch(function () {
          // If we cannot report, we cannot report that we cannot report.
        });
      }
    } catch (_error) {
      /* transport failure is terminal by design */
    } finally {
      // Released on the next tick so the fetch wrapper's own handlers, which run
      // asynchronously, are still inside the guarded window.
      window.setTimeout(function () { reporting = false; }, 0);
    }
  }

  // ── capture: uncaught errors ───────────────────────────────────────────────
  // These are additional listeners; app.js's installFriendlyErrorHandler keeps
  // its own and still shows the user the friendly toast. Multiple listeners for
  // the same event coexist, so nothing there had to change.
  window.addEventListener("error", function (e) {
    try {
      if (!e) return;
      // A failed <script>/<img>/<link> fires "error" on the element with no
      // .error. Those are the cache-busting/deploy failures that make the app
      // look mysteriously half-dead, so they are worth their own kind.
      if (!e.error && e.target && e.target !== window && e.target.tagName) {
        report({
          kind: "resource_error",
          name: "ResourceError",
          message: e.target.tagName + " failed to load",
          source: pathOf(e.target.src || e.target.href || "")
        });
        return;
      }
      if (!e.error) return;
      report({
        kind: "uncaught_error",
        name: redact(e.error.name || "Error", 120),
        message: redact(e.error.message || e.message || "", 500),
        stack: redact(e.error.stack || "", 2000),
        source: pathOf(e.filename || ""),
        line: e.lineno || 0,
        column: e.colno || 0
      });
    } catch (_error) { /* never throw from a handler */ }
  }, true);

  window.addEventListener("unhandledrejection", function (e) {
    try {
      if (!e || !e.reason) return;
      var reason = e.reason;
      var isError = reason instanceof Error;
      report({
        kind: "unhandled_rejection",
        name: redact(isError ? (reason.name || "Error") : typeof reason, 120),
        message: redact(isError ? reason.message : String(reason), 500),
        stack: redact(isError ? (reason.stack || "") : "", 2000)
      });
    } catch (_error) { /* never throw from a handler */ }
  });

  // Content Security Policy failures never become JavaScript exceptions: the
  // browser blocks the resource/action before app code runs. Without this
  // listener a bad CSP deployment can break login, API calls, fonts, or the
  // main bundle while leaving both window.onerror and console.error silent.
  // Do not retain documentURI, originalPolicy, referrer, or sample; all can
  // contain query strings or page data. The directive and blocked URL path are
  // sufficient to diagnose the policy/header that caused the break.
  window.addEventListener("securitypolicyviolation", function (e) {
    try {
      if (!e) return;
      report({
        kind: "security_policy_violation",
        name: "SecurityPolicyViolation",
        message: "Browser security policy blocked " + redact(e.effectiveDirective || e.violatedDirective || "resource", 120),
        source: pathOf(e.blockedURI || ""),
        directive: redact(e.effectiveDirective || e.violatedDirective || "unknown", 120),
        disposition: String(e.disposition || "enforce").toLowerCase() === "report" ? "report" : "enforce",
        status: Number(e.statusCode) || 0,
        line: e.lineNumber || 0,
        column: e.columnNumber || 0
      });
    } catch (_error) { /* never throw from a handler */ }
  });

  // Browser interventions and renderer crashes are delivered through the
  // Reporting API, not the normal error events. Capture only active
  // interventions/crashes; deprecation reports are development advice rather
  // than user-visible failures and would add noise. The report body is not
  // serialized because browsers may add URL-shaped or implementation-specific
  // fields in the future.
  (function installReportingObserver() {
    try {
      if (typeof window.ReportingObserver !== "function") return;
      var observer = new window.ReportingObserver(function (reports) {
        try {
          for (var i = 0; i < reports.length; i += 1) {
            var reportItem = reports[i];
            var type = String(reportItem && reportItem.type || "").toLowerCase();
            if (type !== "intervention" && type !== "crash") continue;
            report({
              kind: type === "crash" ? "browser_crash" : "browser_intervention",
              name: type === "crash" ? "BrowserCrash" : "BrowserIntervention",
              message: type === "crash" ? "Browser renderer reported a crash" : "Browser blocked or changed an unsafe operation",
              source: pathOf(reportItem && reportItem.url || "")
            });
          }
        } catch (_error) { /* observer callback is best effort */ }
      }, { buffered: true, types: ["intervention", "crash"] });
      observer.observe();
    } catch (_error) { /* unsupported ReportingObserver options are harmless */ }
  })();

  // ── capture: failed API calls ──────────────────────────────────────────────
  // app.js has 73 separate fetch() call sites and no central helper, so wrapping
  // window.fetch is the only way to cover them all — and it covers every future
  // one for free. The wrapper is transparent: it returns the original promise
  // and never alters a response or swallows a rejection.
  (function wrapFetch() {
    try {
      if (typeof window.fetch !== "function") return;
      var originalFetch = window.fetch;
      window.fetch = function (input, init) {
        var startedAt = Date.now();
        var method = "GET";
        var rawUrl = "";
        try {
          rawUrl = typeof input === "string" ? input : (input && input.url) || "";
          method = String((init && init.method) || (input && input.method) || "GET").toUpperCase();
        } catch (_error) { /* best effort */ }
        var route = pathOf(rawUrl);

        var result;
        try {
          result = originalFetch.apply(this, arguments);
        } catch (error) {
          // Synchronous throw (bad arguments) — rethrow untouched after noting it.
          report({ kind: "fetch_failed", name: "FetchError", message: redact(error && error.message, 500), requestRoute: route, requestMethod: method });
          throw error;
        }
        if (!result || typeof result.then !== "function") return result;

        return result.then(function (response) {
          try {
            var durationMs = Date.now() - startedAt;
            // Never report the telemetry endpoint's own traffic — that is the
            // loop this whole module is written to avoid.
            if (route.indexOf("/api/client-errors") === -1) {
              if (response && response.status >= 400) {
                report({
                  kind: "http_error",
                  name: "HttpError",
                  message: "HTTP " + response.status + " on " + route,
                  status: response.status,
                  requestRoute: route,
                  requestMethod: method,
                  durationMs: durationMs
                });
              } else if (durationMs >= SLOW_FETCH_MS) {
                report({
                  kind: "other",
                  name: "SlowRequest",
                  message: "Slow response on " + route,
                  status: response && response.status,
                  requestRoute: route,
                  requestMethod: method,
                  durationMs: durationMs
                });
              }
            }
          } catch (_error) { /* reporting must not alter the response */ }
          return response;
        }, function (error) {
          try {
            if (route.indexOf("/api/client-errors") === -1) {
              report({
                kind: "fetch_failed",
                name: redact((error && error.name) || "FetchError", 120),
                message: redact((error && error.message) || "network request failed", 500),
                requestRoute: route,
                requestMethod: method,
                durationMs: Date.now() - startedAt
              });
            }
          } catch (_reportError) { /* ignore */ }
          // Rethrow so callers see exactly what they would have seen.
          throw error;
        });
      };
      window.fetch.__tmsWrapped = true;
    } catch (_error) { /* an unwrappable fetch just means less coverage */ }
  })();

  // ── capture: errors the app caught itself ─────────────────────────────────
  // This portal has no React error boundaries — it is one big app.js — so the
  // equivalent signal is the code that catches a failure, degrades gracefully,
  // and console.errors about it. app.js does exactly that in ~10 places,
  // including the five [tms.boot] guards that fire when the portal comes up
  // broken. Those are the highest-value errors in the product and until now
  // they went nowhere but the user's own devtools. Wrapping console.error
  // collects them without touching app.js at all.
  //
  // PHI: console.error is called with arbitrary arguments, so ONLY strings and
  // Errors are read. Every other argument contributes its TYPE and nothing
  // else — a data object passed to console.error must never be serialised into
  // a report, because in this app a data object is usually a claim.
  (function wrapConsoleError() {
    try {
      if (!window.console || typeof window.console.error !== "function") return;
      var originalError = window.console.error;
      window.console.error = function () {
        try {
          if (!reporting) {
            var parts = [];
            var firstError = null;
            for (var i = 0; i < arguments.length && i < 4; i += 1) {
              var argument = arguments[i];
              if (argument instanceof Error) {
                if (!firstError) firstError = argument;
                parts.push(argument.name + ": " + argument.message);
              } else if (typeof argument === "string") {
                parts.push(argument);
              } else if (argument === null || argument === undefined) {
                parts.push(String(argument));
              } else if (typeof argument === "number" || typeof argument === "boolean") {
                parts.push(String(argument));
              } else {
                // Deliberately opaque. See the PHI note above.
                parts.push(Array.isArray(argument) ? "[array]" : "[" + typeof argument + "]");
              }
            }
            var text = parts.join(" ");
            // app.js's installFriendlyErrorHandler console.errors every uncaught
            // error and rejection (app.js:2373/2378) - which our own window
            // listeners already reported. The kind and message differ, so dedupe
            // never caught it and each crash burned 2 of the 20-event budget.
            var alreadyReported = text.indexOf("[tms.portal] uncaught:") === 0
              || text.indexOf("[tms.portal] unhandled rejection:") === 0;
            if (text && !alreadyReported && text.indexOf("[tms.telemetry]") === -1) {
              report({
                // A [tms.boot] failure means the portal did not come up
                // correctly — that is a boundary trip, not a log line, and it
                // is recorded at error level rather than warn.
                kind: /\[tms\.boot\]/.test(text) ? "boundary" : "console_error",
                name: redact(firstError ? (firstError.name || "Error") : "ConsoleError", 120),
                message: redact(text, 500),
                stack: firstError ? redact(firstError.stack || "", 2000) : ""
              });
            }
          }
        } catch (_error) {
          // Capturing must never break console.error itself.
        }
        // Always pass through: devtools behaviour is completely unchanged.
        return originalError.apply(window.console, arguments);
      };
      window.console.error.__tmsWrapped = true;
    } catch (_error) { /* an unwrappable console just means less coverage */ }
  })();

  // ── flush on the way out ───────────────────────────────────────────────────
  // pagehide/visibilitychange are the reliable pair; unload is not fired on
  // mobile. Beacon survives the teardown that a normal fetch would not.
  window.addEventListener("pagehide", function () { try { flush(true); } catch (_error) {} });
  document.addEventListener("visibilitychange", function () {
    try {
      if (document.visibilityState === "hidden") flush(true);
    } catch (_error) {}
  });

  window.__tmsClientTelemetry = {
    report: report,
    flush: flush,
    sessionId: sessionId,
    // Exposed for the verify harness, which asserts redaction on real payloads.
    _redact: redact,
    _pathOf: pathOf,
    _queue: function () { return queue.slice(); },
    _stats: function () { return { sentCount: sentCount, droppedCount: droppedCount }; }
  };
})();
